import type { TFunction } from "i18next";
import type { DashboardNarrativeContent } from "src/components/common";
import { formatOrganicPostTypeLabel } from "../organicMediaDefinitions";
import type {
  OrganicSocialPlatform,
  SocialReportBundle,
  SocialTotals,
  SocialTypePerformance,
} from "../../../data/organicSocialMockData";

interface SummaryFormatters {
  formatCount: (value: number) => string;
  formatPercent: (value: number | null) => string;
  isRTL: boolean;
}

interface BuildSummaryParams {
  platform: OrganicSocialPlatform;
  platformLabel: string;
  report: SocialReportBundle;
  startDate: string;
  endDate: string;
  fmt: SummaryFormatters;
  t: TFunction;
}

/** Wraps a formatted number/percent in `++...++` or `--...--` so `DashboardNarrativePanel`
 * renders it green/red inline, based on this specific metric's own delta — not the
 * sentence's overall tone. `null`/flat deltas render uncolored. */
const colorize = (formattedValue: string, delta: number | null): string => {
  if (delta === null || Math.abs(delta) < 2) {
    return formattedValue;
  }
  return delta > 0 ? `++${formattedValue}++` : `--${formattedValue}--`;
};

const changePercent = (current: number, previous: number): number | null => {
  if (previous === 0) {
    return current === 0 ? 0 : null;
  }
  return Math.round(((current - previous) / previous) * 1000) / 10;
};

function movement(delta: number | null, isRTL: boolean): string {
  if (delta === null) {
    return isRTL ? "تغيّر" : "changed";
  }
  const abs = Math.abs(delta);
  const direction = delta >= 0 ? (isRTL ? "ارتفع" : "rose") : (isRTL ? "انخفض" : "fell");
  const stable = isRTL ? "ظل مستقراً نسبياً" : "stayed broadly flat";

  if (abs < 2) {return stable;}
  if (abs < 5) {return isRTL ? `${direction} قليلاً` : `${direction} slightly`;}
  if (abs <= 15) {return direction;}
  return isRTL ? `${direction} بشكل ملحوظ` : `${direction} sharply`;
}

const engagementsFor = (totals: SocialTotals) => totals.likes + totals.comments + totals.shares;

const topTypeByMetric = <K extends keyof SocialTypePerformance>(
  typePerformance: SocialTypePerformance[],
  key: K,
): SocialTypePerformance | undefined =>
  [...typePerformance].sort((a, b) => (Number(b[key]) || 0) - (Number(a[key]) || 0))[0];

function buildLeadSummary(params: BuildSummaryParams): string {
  const { report, fmt, platformLabel } = params;
  const { isRTL } = fmt;
  const { totals, priorTotals } = report;

  const followerDelta = changePercent(totals.followers, priorTotals.followers);
  const followersText = colorize(fmt.formatCount(totals.followers), followerDelta);
  const growthText = colorize(fmt.formatCount(totals.followerGrowth), followerDelta);

  const engagementDelta = changePercent(engagementsFor(totals), engagementsFor(priorTotals));
  const engagementsText = colorize(fmt.formatCount(engagementsFor(totals)), engagementDelta);
  const engagementRateText = colorize(fmt.formatPercent(totals.engagementRate), engagementDelta);

  const topType = topTypeByMetric(report.typePerformance, "impressions");

  return isRTL
    ? `بلغ عدد متابعي ${platformLabel} ${followersText}، حيث ${movement(followerDelta, isRTL)} مقارنة بالفترة السابقة، بإضافة ${growthText} متابع جديد. حقق المحتوى ${engagementsText} تفاعل بمعدل تفاعل ${engagementRateText}، وقد ${movement(engagementDelta, isRTL)} مقارنة بالفترة السابقة.${topType ? ` يتصدر محتوى ${formatOrganicPostTypeLabel(topType.key, params.t)} من حيث مرات الظهور.` : ""}`
    : `${platformLabel} followers reached ${followersText}, having ${movement(followerDelta, isRTL)} versus the prior period, with ${growthText} new followers gained. Content generated ${engagementsText} engagements at a ${engagementRateText} engagement rate, having ${movement(engagementDelta, isRTL)} versus the prior period.${topType ? ` ${formatOrganicPostTypeLabel(topType.key, params.t)} content leads by impressions.` : ""}`;
}

function buildInsights(params: BuildSummaryParams): string[] {
  const { report, fmt, t } = params;
  const { isRTL } = fmt;
  const { totals, priorTotals, typePerformance, content } = report;
  const insights: string[] = [];

  const followerDelta = changePercent(totals.followers, priorTotals.followers);
  const followersText = colorize(fmt.formatCount(totals.followers), followerDelta);
  const growthText = colorize(fmt.formatCount(totals.followerGrowth), followerDelta);
  insights.push(
    isRTL
      ? `عدد المتابعين ${followersText}، وقد ${movement(followerDelta, isRTL)} بنسبة نمو صافية قدرها ${growthText} متابع.`
      : `Followers stand at ${followersText}, having ${movement(followerDelta, isRTL)} with net growth of ${growthText}.`,
  );

  const impressionsDelta = changePercent(totals.impressions, priorTotals.impressions);
  const impressionsText = colorize(fmt.formatCount(totals.impressions), impressionsDelta);
  insights.push(
    isRTL
      ? `مرات الظهور ${movement(impressionsDelta, isRTL)} إلى ${impressionsText}، بإجمالي ${fmt.formatCount(totals.views)} مشاهدة.`
      : `Impressions ${movement(impressionsDelta, isRTL)} to ${impressionsText}, across ${fmt.formatCount(totals.views)} total views.`,
  );

  const ctrDelta = changePercent(totals.ctr ?? 0, priorTotals.ctr ?? 0);
  const ctrText = colorize(fmt.formatPercent(totals.ctr), ctrDelta);
  insights.push(
    isRTL
      ? `معدل النقر إلى الظهور ${ctrText}، وقد ${movement(ctrDelta, isRTL)} مقارنة بالفترة السابقة.`
      : `Click-through rate is ${ctrText}, having ${movement(ctrDelta, isRTL)} versus the prior period.`,
  );

  const topEngagement = topTypeByMetric(typePerformance, "engagementRate");
  if (topEngagement) {
    insights.push(
      isRTL
        ? `يحقق محتوى ${formatOrganicPostTypeLabel(topEngagement.key, t)} أعلى معدل تفاعل بنسبة ${fmt.formatPercent(topEngagement.engagementRate)}.`
        : `${formatOrganicPostTypeLabel(topEngagement.key, t)} content drives the highest engagement rate at ${fmt.formatPercent(topEngagement.engagementRate)}.`,
    );
  }

  const topClicks = topTypeByMetric(typePerformance, "ctr");
  if (topClicks) {
    insights.push(
      isRTL
        ? `يسجل محتوى ${formatOrganicPostTypeLabel(topClicks.key, t)} أعلى معدل نقر إلى الظهور عند ${fmt.formatPercent(topClicks.ctr)}.`
        : `${formatOrganicPostTypeLabel(topClicks.key, t)} content posts the strongest click-through rate at ${fmt.formatPercent(topClicks.ctr)}.`,
    );
  }

  const contentDelta = changePercent(totals.contentCreated, priorTotals.contentCreated);
  const contentCountText = colorize(fmt.formatCount(totals.contentCreated), contentDelta);
  insights.push(
    isRTL
      ? `عدد المنشورات ${contentCountText}، وقد ${movement(contentDelta, isRTL)} مقارنة بالفترة السابقة.`
      : `Content volume was ${contentCountText} posts, having ${movement(contentDelta, isRTL)} versus the prior period.`,
  );

  const topPost = [...content].sort((a, b) => b.impressions - a.impressions)[0];
  if (topPost) {
    insights.push(
      isRTL
        ? `أفضل منشور من حيث الأداء حقق ${fmt.formatCount(topPost.impressions)} ظهور و${fmt.formatCount(topPost.likes + topPost.comments + topPost.shares)} تفاعل.`
        : `The top-performing post drew ${fmt.formatCount(topPost.impressions)} impressions and ${fmt.formatCount(topPost.likes + topPost.comments + topPost.shares)} engagements.`,
    );
  }

  return insights.slice(0, 8);
}

function buildAction(params: BuildSummaryParams): string | null {
  const { report, fmt, t } = params;
  const { isRTL } = fmt;
  const { typePerformance } = report;

  const bestEngagement = topTypeByMetric(typePerformance, "engagementRate");
  const worstEngagement = [...typePerformance].sort(
    (a, b) => (a.engagementRate ?? 0) - (b.engagementRate ?? 0),
  )[0];

  if (bestEngagement && worstEngagement && bestEngagement.key !== worstEngagement.key) {
    return isRTL
      ? `زد وتيرة نشر محتوى ${formatOrganicPostTypeLabel(bestEngagement.key, t)} على حساب محتوى ${formatOrganicPostTypeLabel(worstEngagement.key, t)} الأقل تفاعلاً.`
      : `Shift publishing cadence toward ${formatOrganicPostTypeLabel(bestEngagement.key, t)} content and away from lower-performing ${formatOrganicPostTypeLabel(worstEngagement.key, t)} posts.`;
  }

  return isRTL
    ? "استمر في نشر المحتوى الأعلى أداءً لتعزيز نمو المتابعين."
    : "Keep publishing the best-performing content format to sustain follower growth.";
}

function formatPeriodLabel(startIso: string, endIso: string, isRTL: boolean): string {
  const start = new Date(`${startIso}T00:00:00Z`);
  const end = new Date(`${endIso}T00:00:00Z`);
  const formatter = new Intl.DateTimeFormat(isRTL ? "ar-EG-u-nu-arab" : "en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
  return `${formatter.format(start)} – ${formatter.format(end)}`;
}

export interface SocialReportExecutiveSummary extends DashboardNarrativeContent {
  hasData: boolean;
  periodLabel: string;
  action: string | null;
}

export function generateSocialReportExecutiveSummary(
  params: BuildSummaryParams,
): SocialReportExecutiveSummary {
  const { report, startDate, endDate, fmt } = params;
  const periodLabel = formatPeriodLabel(startDate, endDate, fmt.isRTL);
  const hasData = report.totals.impressions > 0 || report.totals.followers > 0;

  if (!hasData) {
    return {
      summary: "",
      keyInsights: [],
      recommendations: [],
      hasData: false,
      periodLabel,
      action: null,
    };
  }

  const leadSummary = buildLeadSummary(params);
  const insights = buildInsights(params);
  const action = buildAction(params);

  const lowData = report.content.length < 5 || report.daily.length < 7;
  const lowDataPrefix = lowData
    ? (fmt.isRTL ? "بيانات محدودة لهذه الفترة — " : "Limited data for this period — ")
    : "";

  return {
    summary: `${lowDataPrefix}${leadSummary}`,
    keyInsights: insights,
    recommendations: action ? [action] : [],
    hasData: true,
    periodLabel,
    action,
  };
}
